import { useEffect, useState } from 'react';
import type { FormEvent, JSX } from 'react';

import { Link, useNavigate } from 'react-router-dom';

import { Button } from '../../components/Button/Button';
import { Input } from '../../components/Inputs/Input';
import { Label } from '../../components/Inputs/Label';
import { SingleSelect } from '../../components/Inputs/SingleSelect';
import RevvlyMark from '../../components/Layout/revvly-mark.svg?react';
import { COUNTRY_OPTIONS } from '../Settings/Tax/taxFormat';

import styles from './Signup.module.css';
import { fetchSignupOptions, requestSignup, SignupApiError } from './signupApi';
import type { SignupOptions } from './signupApi';

const PERIODS = [
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'YEARLY', label: 'Yearly' },
];

/**
 * Start a free trial. Nothing is created here: the form only records the
 * details and emails a link, and the workspace is made when the link is used
 * (see SignupConfirm). The page says the same thing whether or not the address
 * is already known, so it cannot be used to find out who has an account.
 */
export default function Signup(): JSX.Element {
  const navigate = useNavigate();
  const [options, setOptions] = useState<SignupOptions | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [country, setCountry] = useState('');
  const [period, setPeriod] = useState<'MONTHLY' | 'YEARLY'>('MONTHLY');
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [website, setWebsite] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    fetchSignupOptions()
      .then(setOptions)
      .catch(() => setLoadFailed(true));
  }, []);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!country) {
      setError('Choose your country.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await requestSignup({
        email,
        name,
        company,
        country,
        period,
        website,
        ...(companyWebsite.trim() && { companyWebsite: companyWebsite.trim() }),
      });
      setSent(true);
    } catch (err) {
      setError(err instanceof SignupApiError ? err.message : 'Something went wrong. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const closed = loadFailed || (options && (!options.signupEnabled || options.plans.length === 0));

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <RevvlyMark className={styles.mark} aria-hidden="true" />
        <h1 className={styles.title}>Start your free trial</h1>

        {sent ? (
          <>
            <p className={styles.subtitle}>
              Check your email. We sent a link to {email} to finish setting up. It works once and
              expires soon.
            </p>
            <div className={styles.actions}>
              <Button variant="primary" onClick={() => navigate('/login')}>
                Back to sign in
              </Button>
            </div>
          </>
        ) : closed ? (
          <p className={styles.subtitle}>
            Signup is not open right now. If you already have an account, sign in below.
          </p>
        ) : (
          <>
            <p className={styles.subtitle}>
              {options
                ? `${options.trialDays} days free. No card needed until the trial ends.`
                : 'One moment.'}
            </p>
            <form className={styles.form} onSubmit={submit}>
              <div>
                <Label htmlFor="signup-name">Your name</Label>
                <Input
                  id="signup-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  minLength={2}
                  maxLength={100}
                  autoComplete="name"
                />
              </div>
              <div>
                <Label htmlFor="signup-email">Work email</Label>
                <Input
                  id="signup-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
              </div>
              <div>
                <Label htmlFor="signup-company">Company</Label>
                <Input
                  id="signup-company"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  required
                  minLength={2}
                  maxLength={100}
                  autoComplete="organization"
                />
              </div>
              <div>
                <Label htmlFor="signup-website">Company website (optional)</Label>
                <Input
                  id="signup-website"
                  value={companyWebsite}
                  onChange={(e) => setCompanyWebsite(e.target.value)}
                  maxLength={200}
                  placeholder="acme.com"
                  autoComplete="url"
                  inputMode="url"
                />
              </div>
              <div>
                <Label htmlFor="signup-country">Country</Label>
                <SingleSelect
                  id="signup-country"
                  options={COUNTRY_OPTIONS}
                  value={country}
                  onChange={(value) => setCountry(String(value))}
                  placeholder="Choose a country"
                  searchable
                  required
                />
              </div>
              <div>
                <Label htmlFor="signup-period">Billing</Label>
                <SingleSelect
                  id="signup-period"
                  options={PERIODS}
                  value={period}
                  onChange={(value) => setPeriod(value as 'MONTHLY' | 'YEARLY')}
                />
              </div>
              <div className={styles.trap} aria-hidden="true">
                <Input
                  tabIndex={-1}
                  autoComplete="off"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  aria-label="Leave this field empty"
                />
              </div>
              {error && (
                <p className={styles.error} role="alert">
                  {error}
                </p>
              )}
              <Button type="submit" disabled={submitting || !options}>
                {submitting ? 'Sending' : 'Send me the link'}
              </Button>
            </form>
          </>
        )}

        {!sent && (
          <p className={styles.fine}>
            Already have an account?{' '}
            <Link className={styles.link} to="/login">
              Sign in
            </Link>
          </p>
        )}
      </div>
    </div>
  );
}
